import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { apiCall } from '../../utils/auth';
import { API_ENDPOINTS } from '../../config/clerkApi';
import './QuizTab.css';

const QuizTab = ({ video }) => {
  const { getToken } = useAuth();
  
  // Quiz state
  const [quizData, setQuizData] = useState(null);
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [revealedAnswers, setRevealedAnswers] = useState({});
  const [aiExplanations, setAiExplanations] = useState({});
  const [showResults, setShowResults] = useState(false);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [savedResults, setSavedResults] = useState(null);
  const [quizLoading, setQuizLoading] = useState(false);
  const [quizError, setQuizError] = useState(null);
  const [explanationLoading, setExplanationLoading] = useState({});

  // Generate new quiz
  const generateQuiz = useCallback(async () => {
    if (!video?.uuid_video) return;
    
    setQuizLoading(true);
    setQuizError(null);
    
    try {
      const response = await apiCall(API_ENDPOINTS.QUIZ, {
        method: 'POST',
        body: JSON.stringify({
          video_uuid: video.uuid_video
        })
      }, getToken);

      if (response.ok) {
        const data = await response.json();
        setQuizData(data.quiz.quiz_json || []);
        setSelectedAnswers({});
        setAiExplanations({});
        setRevealedAnswers({});
        setShowResults(false);
        setQuizCompleted(false);
        setSavedResults(null);
        console.log('✅ Quiz generated successfully:', data);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate quiz');
      }
    } catch (error) {
      console.error('❌ Quiz generation error:', error);
      setQuizError(error.message);
    } finally {
      setQuizLoading(false);
    }
  }, [video?.uuid_video, getToken]);

  // Submit quiz results
  const submitQuizResults = useCallback(async (answers) => {
    if (!video?.uuid_video || !answers) return;
    
    try {
      // Convert answers object to array
      const userAnswersArray = [];
      for (let i = 0; i < quizData.length; i++) {
        userAnswersArray.push(answers[i] !== undefined ? answers[i] : -1);
      }
      
      const response = await apiCall(API_ENDPOINTS.QUIZ_SUBMIT, {
        method: 'POST',
        body: JSON.stringify({
          video_uuid: video.uuid_video,
          user_answers: userAnswersArray
        })
      }, getToken);

      if (response.ok) {
        const data = await response.json();
        setQuizCompleted(true);
        setSavedResults(data.results);
        setShowResults(true);
        console.log('✅ Quiz results saved successfully:', data);
      } else {
        const errorData = await response.json();
        console.error('❌ Failed to save quiz results:', errorData.error);
      }
    } catch (error) {
      console.error('❌ Error submitting quiz results:', error);
    }
  }, [video?.uuid_video, quizData, getToken]);

  // Save progress with debouncing
  const saveProgress = useCallback(async () => {
    if (!video?.uuid_video) return;
    
    // Only save if there are answers, explanations, or revealed answers
    if (Object.keys(selectedAnswers).length === 0 && 
        Object.keys(aiExplanations).length === 0 && 
        Object.keys(revealedAnswers).length === 0) {
      return;
    }
    
    try {
      const response = await apiCall(API_ENDPOINTS.QUIZ_PROGRESS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          video_uuid: video.uuid_video,
          partial_answers: selectedAnswers,
          ai_explanations: aiExplanations,
          revealed_answers: revealedAnswers
        }),
      }, getToken);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Progress saved:', data);
      } else {
        console.error('❌ Failed to save progress:', response.status);
      }
    } catch (error) {
      console.error('❌ Error saving progress:', error);
    }
  }, [video?.uuid_video, selectedAnswers, aiExplanations, revealedAnswers, getToken]);

  // Debounced save - triggers 3 seconds after changes
  useEffect(() => {
    // Return early if no data to save
    if (Object.keys(selectedAnswers).length === 0 && 
        Object.keys(aiExplanations).length === 0 && 
        Object.keys(revealedAnswers).length === 0) {
      return;
    }

    const timeoutId = setTimeout(() => {
      saveProgress();
    }, 3000); // Increased to 3 seconds to reduce requests

    return () => clearTimeout(timeoutId);
  }, [selectedAnswers, aiExplanations, revealedAnswers]); // Removed saveProgress from dependencies

  // Load quiz when video changes
  useEffect(() => {
    const videoId = video?.uuid_video;
    if (!videoId) return;

    // Reset state
    setSelectedAnswers({});
    setRevealedAnswers({});
    setAiExplanations({});
    setShowResults(false);
    setQuizCompleted(false);
    setSavedResults(null);
    setExplanationLoading({});
    
    const abortController = new AbortController();
    let isMounted = true;
    
    const fetchData = async () => {
      if (!isMounted) return;
      
      setQuizError(null);
      
      try {
        const response = await apiCall(`${API_ENDPOINTS.QUIZ}?video_uuid=${videoId}`, {
          method: 'GET'
        }, getToken);

        if (abortController.signal.aborted) return;

        if (response.status === 404) {
          if (isMounted) {
            setQuizData(null);
            setQuizCompleted(false);
            setSavedResults(null);
          }
          return;
        }

        if (response.ok && isMounted) {
          const data = await response.json();
          setQuizData(data.quiz.quiz_json || []);
          
          // Check if quiz is completed
          if (data.completion && data.completion.is_completed) {
            setQuizCompleted(true);
            setSavedResults(data.completion);
            setShowResults(true);
            
            // Reconstruct selected answers from saved results
            const reconstructedAnswers = {};
            if (data.completion.user_answers) {
              data.completion.user_answers.forEach(answer => {
                reconstructedAnswers[answer.question_index] = answer.selected_answer;
              });
              setSelectedAnswers(reconstructedAnswers);
            }
          } 
          // Check for partial progress
          else if (data.partial_progress && data.partial_progress.has_progress) {
            console.log('🔄 Loading saved progress:', data.partial_progress);
            
            if (data.partial_progress.partial_answers) {
              setSelectedAnswers(data.partial_progress.partial_answers);
            }
            
            if (data.partial_progress.ai_explanations) {
              setAiExplanations(data.partial_progress.ai_explanations);
            }
            
            if (data.partial_progress.revealed_answers) {
              setRevealedAnswers(data.partial_progress.revealed_answers);
            }
            
            setQuizCompleted(false);
            setSavedResults(null);
            setShowResults(false);
          }
          else {
            setQuizCompleted(false);
            setSavedResults(null);
          }
          
          console.log('✅ Quiz loaded successfully:', data);
        } else {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to load quiz');
        }
      } catch (error) {
        if (error.name === 'AbortError') {
          console.log('Quiz fetch aborted');
          return;
        }
        if (isMounted) {
          console.error('❌ Quiz loading error:', error);
          setQuizError(error.message);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [video?.uuid_video, getToken]);

  // Event handlers
  const handleAnswerSelect = (questionIndex, optionIndex) => {
    if (showResults) return;
    
    setSelectedAnswers({
      ...selectedAnswers,
      [questionIndex]: optionIndex
    });
  };

  const handleRevealAnswer = (questionIndex) => {
    setRevealedAnswers({
      ...revealedAnswers,
      [questionIndex]: true
    });
  };

  const handleAskAI = async (questionIndex) => {
    if (explanationLoading[questionIndex]) return;

    setExplanationLoading({
      ...explanationLoading,
      [questionIndex]: true
    });

    try {
      const question = quizData[questionIndex];
      const userAnswerIndex = selectedAnswers[questionIndex];
      const userAnswer = question.answers[userAnswerIndex];
      const correctAnswer = question.answers[question.correct_index];

      const requestBody = {
        question: question.question,
        user_answer: userAnswer,
        correct_answer: correctAnswer,
        context: "Brief explanation why the answer is wrong"
      };

      const response = await apiCall(API_ENDPOINTS.QUIZ_EXPLAIN, {
        method: 'POST',
        body: JSON.stringify(requestBody)
      }, getToken);

      if (response.ok) {
        const data = await response.json();
        setAiExplanations({
          ...aiExplanations,
          [questionIndex]: data.explanation
        });
      } else {
        console.error('❌ Failed to get AI explanation');
      }
    } catch (error) {
      console.error('❌ Error getting AI explanation:', error);
    } finally {
      setExplanationLoading({
        ...explanationLoading,
        [questionIndex]: false
      });
    }
  };

  const handleSubmitQuiz = () => {
    // Check if all questions are answered
    const unansweredQuestions = [];
    for (let i = 0; i < quizData.length; i++) {
      if (selectedAnswers[i] === undefined) {
        unansweredQuestions.push(i + 1);
      }
    }

    if (unansweredQuestions.length > 0) {
      alert(`Please answer all questions before submitting. Missing: ${unansweredQuestions.join(', ')}`);
      return;
    }

    submitQuizResults(selectedAnswers);
  };

  const calculateScore = () => {
    if (savedResults) {
      const correct = savedResults.correct_answers_count || 0;
      const total = savedResults.total_questions || 0;
      const percentage = savedResults.score_percentage ? parseFloat(savedResults.score_percentage).toFixed(1) : '0.0';
      
      return { correct, total, percentage };
    }
    
    if (!quizData || !Array.isArray(quizData)) {
      return { correct: 0, total: 0, percentage: '0.0' };
    }
    
    let correctAnswers = 0;
    quizData.forEach((question, index) => {
      if (selectedAnswers[index] === question.correct_index) {
        correctAnswers++;
      }
    });
    
    const total = quizData.length;
    const percentage = total > 0 ? ((correctAnswers / total) * 100).toFixed(1) : '0.0';
    
    return { correct: correctAnswers, total, percentage };
  };

  // Loading state
  if (quizLoading) {
    return (
      <div className="loading-content">
        <div className="loading-spinner" />
        <div className="loading-text">Generating quiz...</div>
      </div>
    );
  }

  // Error state
  if (quizError) {
    return (
      <div className="error-content">
        <p>Error: {quizError}</p>
        <button className="control-button" onClick={() => window.location.reload()}>
          Try Again
        </button>
      </div>
    );
  }

  // No quiz state
  if (!quizData || !Array.isArray(quizData) || quizData.length === 0) {
    return (
      <div className="no-content">
        <button className="control-button" onClick={generateQuiz}>
          Generate Quiz
        </button>
      </div>
    );
  }

  // Results view
  if (showResults) {
    const score = calculateScore();
    return (
      <div className="quiz-container">
        <div className="quiz-results">
          <div className="quiz-score">
            <h2>Quiz Results</h2>
            <p>Score: {score.correct} out of {score.total} ({score.percentage}%)</p>
          </div>
          
          <div className="quiz-answers">
            {quizData.map((question, index) => (
              <div key={index} className="quiz-answer-review">
                <div className="question-text">
                  Question {index + 1}: {question.question}
                </div>
                <div className="options-container">
                  {question.answers.map((option, optionIndex) => (
                    <div
                      key={optionIndex}
                      className={`option-button ${
                        selectedAnswers[index] === optionIndex
                          ? optionIndex === question.correct_index
                            ? 'correct'
                            : 'incorrect'
                          : optionIndex === question.correct_index
                          ? 'correct'
                          : ''
                      }`}
                    >
                      {option}
                    </div>
                  ))}
                </div>
                
                <div className="ask-ai-container">
                  <button
                    className="ask-ai-button"
                    onClick={() => handleAskAI(index)}
                    disabled={explanationLoading[index]}
                  >
                    {explanationLoading[index] ? (
                      <>
                        <div className="loading-spinner-small" />
                        Generating explanation...
                      </>
                    ) : (
                      'Ask AI'
                    )}
                  </button>
                </div>
                
                {aiExplanations[index] && (
                  <div className="ai-explanation">
                    <div className="ai-explanation-text">
                      {aiExplanations[index]}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
          
          {!quizCompleted && (
            <button
              className="control-button"
              onClick={() => {
                setSelectedAnswers({});
                setShowResults(false);
              }}
            >
              Try Again
            </button>
          )}
        </div>
      </div>
    );
  }

  // Quiz view - show all questions at once
  return (
    <div className="quiz-container">
      <div className="quiz-content">
        <div className="quiz-header">
          <h3>Quiz ({quizData.length} questions)</h3>
          <p>Answer all questions and click submit when ready.</p>
        </div>
        
        <div className="all-questions-container">
          {quizData.map((question, questionIndex) => {
            if (!question || !Array.isArray(question.answers)) {
              return (
                <div key={questionIndex} className="question-error">
                  <p>Invalid question data for question {questionIndex + 1}</p>
                </div>
              );
            }

            return (
              <div key={questionIndex} className="question-block">
                <div className="question-header">
                  <span className="question-number">Question {questionIndex + 1}</span>
                </div>
                <div className="question-text">
                  {question.question}
                </div>
                <div className="options-container">
                  {question.answers.map((option, optionIndex) => (
                    <button
                      key={optionIndex}
                      className={`option-button ${
                        selectedAnswers[questionIndex] === optionIndex ? 'selected' : ''
                      } ${
                        revealedAnswers[questionIndex] && optionIndex === question.correct_index ? 'correct' : ''
                      } ${
                        revealedAnswers[questionIndex] && 
                        selectedAnswers[questionIndex] === optionIndex && 
                        optionIndex !== question.correct_index ? 'incorrect' : ''
                      }`}
                      onClick={() => handleAnswerSelect(questionIndex, optionIndex)}
                    >
                      <span className="option-letter">{String.fromCharCode(65 + optionIndex)}</span>
                      {option}
                    </button>
                  ))}
                </div>

                {!revealedAnswers[questionIndex] && (
                  <div className="show-answer-container">
                    <button
                      className="show-answer-button"
                      onClick={() => handleRevealAnswer(questionIndex)}
                      disabled={selectedAnswers[questionIndex] === undefined}
                    >
                      Answer
                    </button>
                  </div>
                )}

                {revealedAnswers[questionIndex] && 
                 selectedAnswers[questionIndex] !== undefined && 
                 selectedAnswers[questionIndex] !== question.correct_index &&
                 !aiExplanations[questionIndex] && (
                  <div className="ask-ai-container">
                    <button
                      className="ask-ai-button"
                      onClick={() => handleAskAI(questionIndex)}
                      disabled={explanationLoading[questionIndex]}
                    >
                      {explanationLoading[questionIndex] ? (
                        <>
                          <div className="loading-spinner-small"></div>
                          Generating...
                        </>
                      ) : (
                        'Ask AI'
                      )}
                    </button>
                  </div>
                )}

                {aiExplanations[questionIndex] && (
                  <div className="ai-explanation">
                    <div className="ai-explanation-text">
                      {aiExplanations[questionIndex]}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="quiz-submit-container">
          <div className="quiz-progress">
            Answered: {Object.keys(selectedAnswers).length} / {quizData.length}
          </div>
          <button
            className="submit-quiz-button"
            onClick={handleSubmitQuiz}
            disabled={Object.keys(selectedAnswers).length !== quizData.length}
          >
            Submit Quiz
          </button>
        </div>
      </div>
    </div>
  );
};

export default QuizTab; 